import React, { useEffect, useState } from 'react';
import {
  Box,
  Snackbar,
  Alert,
  Button,
  Radio,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { NoContent, ReactTable } from '../../../components/shared';
import ConfirmationModal from '../../../components/ConfirmationModel';
import { useDeleteLeaseHistoryMutation, useGetLeaseHistoryByIdQuery } from '../../../services/Lease/LeaseService';
import { LEASE_HISTORY_COLUMNS } from '../../../constants/tableColumns';
import { skipToken } from '@reduxjs/toolkit/query';
import useStyles from '../../ManageIndepenantRep/style';
import { LeaseDetailsVersionView } from '../../Lease/LeaseDetails/LeaseDetailsVersionView';
import { getTimeFormat, getDateFormat } from '../../../utils/common-methods';
import { formatChanges } from '../../../utils/formatters/leaseChangeFormatter';
import { formatSummaryText } from '../../../utils/formatters/textSummaryFormatter';
import { Delete } from '@mui/icons-material';
import { routeConstants } from '../../../constants/routeConstants';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 30;

export default function LeaseHistory({ leaseId, open }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const updatedById = useSelector((state) => state.user?.userDetails?.id);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [perPageNumber, setPerPageNumber] = useState(DEFAULT_PER_PAGE);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [showLeaseDetailsModal, setShowLeaseDetailsModal] = useState(false);
  const [selectedLeaseDetails, setSelectedLeaseDetails] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLeaseHistory, { isLoading: isDeleting }] = useDeleteLeaseHistoryMutation();

  const { data: leaseHistoryData, refetch } = useGetLeaseHistoryByIdQuery(
    open ? { leaseId, page: currentPage, perPage: perPageNumber } : skipToken,
  );

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleRadioClick = (row, index) => {
    setSelectedRowIndex(index);
  };

  const handleActionClick = (actionType) => {
    const selectedRow = tableData[selectedRowIndex];
    if (!selectedRow) {
      showSnackbar('Please select a row first.', 'warning');
      return;
    }

    navigate(`/${routeConstants.LEASE_ROUTE}/${routeConstants.EDIT_LEASE_ROUTE}/${leaseId}`, {
      state: {
        version: selectedRow.version,
        type: actionType,
        isLeaseHistoryAction: true,
      },
    });
  };

  const handleViewClick = () => {
    const selectedRow = tableData[selectedRowIndex];
    if (selectedRow) {
      setSelectedLeaseDetails({
        leaseId: leaseId,
        version: selectedRow.version,
      });
      setShowLeaseDetailsModal(true);
    }
  };

  const handleCloseLeaseDetailsModal = () => {
    setShowLeaseDetailsModal(false);
    setSelectedLeaseDetails(null);
  };

  const handleDeleteClick = (targetRow = null) => {
    const selectedRow = targetRow || tableData[selectedRowIndex];
    if (!selectedRow) {
      showSnackbar('Please select a row first.', 'warning');
      return;
    }
    setRowToDelete(selectedRow);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmDeleteOpen(false);
    setRowToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteLeaseHistory({
        leaseId,
        version: rowToDelete.version,
        updatedBy: updatedById,
      }).unwrap();

      showSnackbar('History record deleted successfully.', 'success');
      handleConfirmClose();
      refetch();
    } catch (err) {
      showSnackbar(err?.data?.message || 'Failed to delete history record.', 'error');
    }
  };

  useEffect(() => {
    const mappedData = (leaseHistoryData?.data ?? []).map((item) => ({
      leaseName: item.lease_name,
      sublocationName: item.sublocationNames?.join(', ') || '',
      inEffectFrom: item.in_effect_from ? getDateFormat(item.in_effect_from) : '',
      inEffectTo: item.in_effect_to ? getDateFormat(item.in_effect_to) : '',
      changes: formatChanges(item.changes),
      summary: formatSummaryText(item.summary || 'N/A'),
      modifiedBy:
        item.updatedById && (item.updatedById.firstName || item.updatedById.lastName)
          ? `${item.updatedById.firstName || ''} ${item.updatedById.lastName || ''}`.trim()
          : 'N/A',
      modifiedAt: item.updatedAt ? `${getDateFormat(item.updatedAt)} ${getTimeFormat(item.updatedAt)}` : '',
      indicator: item?.indicator,
      version: item?.version || 1,
    }));
    setTableData(mappedData);
    setTotalCount(leaseHistoryData?.pagination?.total ?? mappedData.length);

    if (mappedData.length > 0) {
      setSelectedRowIndex(0);
    }
  }, [leaseHistoryData]);

  const getRowErrorClass = (rowData) => (rowData?.original?.indicator === 'active' ? classes.activeRow : '');

  const pageChangeHandler = (newPage) => {
    setCurrentPage(newPage);
  };

  const perPageChangeHandler = (newPerPage) => {
    setPerPageNumber(newPerPage);
  };

  const enhancedColumns = [
    {
      Header: '',
      accessor: 'radio',
      width: 50,
      disableSortBy: true,
      Cell: ({ row }) => (
        <Radio
          checked={selectedRowIndex === row.index}
          onChange={() => handleRadioClick(row.original, row.index)}
          name="lease-history-radio"
          color="primary"
        />
      ),
    },
    ...LEASE_HISTORY_COLUMNS,
    {
      Header: '',
      accessor: 'delete',
      width: 50,
      Cell: ({ row }) =>
        row.original.version && row.original.indicator !== 'active' ? (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDeleteClick(row.original)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          ''
        ),
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box display="flex" mb={2} justifyContent="space-between" alignItems="center">
        <Typography variant="h6"></Typography>
        {tableData.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" color="primary" onClick={() => handleActionClick('edit')}>
              Edit
            </Button>
            <Button variant="contained" color="primary" onClick={() => handleActionClick('add')}>
              Add
            </Button>
            <Button variant="contained" color="primary" onClick={handleViewClick} disabled={selectedRowIndex === null}>
              View
            </Button>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
        }}
      >
        {tableData?.length === 0 ? (
          <NoContent title="No Results Found" desc={<span>No results found.</span>} />
        ) : (
          <>
            {totalCount <= perPageNumber && (
              <style>
                {`
                  .MuiTablePagination-root {
                    display: none !important;
                  }
                `}
              </style>
            )}
            <ReactTable
              data={tableData}
              currentPage={currentPage - 1}
              perPage={perPageNumber}
              totalCount={totalCount}
              columns={enhancedColumns}
              rowClassName={(rowData) => getRowErrorClass(rowData)}
              onPageChange={pageChangeHandler}
              pagination={true}
              manualPagination={true}
              changeRowsPerPageOptions={true}
              onPerPageChange={perPageChangeHandler}
            />
          </>
        )}
      </Box>
      <Dialog
        open={showLeaseDetailsModal}
        onClose={handleCloseLeaseDetailsModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseLeaseDetailsModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedLeaseDetails && (
            <LeaseDetailsVersionView leaseId={selectedLeaseDetails.leaseId} version={selectedLeaseDetails.version} />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={confirmDeleteOpen}
        title="Confirm Deletion"
        msg={`Are you sure you want to delete the history record from ${rowToDelete?.inEffectFrom || 'N/A'} to ${
          rowToDelete?.inEffectTo || 'present'
        }? This action cannot be undone.`}
        buttons={[
          { text: isDeleting ? 'Deleting...' : 'Delete', value: 'delete' },
          { text: 'Cancel', value: 'cancel' },
        ]}
        onClick={(value) => {
          if (value === 'delete' && !isDeleting) {
            handleConfirmDelete();
          } else {
            handleConfirmClose();
          }
        }}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
